import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import moment from 'moment';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { toast } from 'react-toastify';
import { Glyphicon, OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import {
  FridgeHealth,
  CreateQueueMessage,
  CompleteComputerSwapRequest,
  GetFridgeEvents,
  Location,
  GetQueueMessages,
  GetFridgeSegmentEvents,
} from '../libs/GraphQL';
import LoaderButton from '../components/LoaderButton';
import styledComponent from '../utils/styledComponent';
import theme from '../theme';
import ConfirmationModal from '../components/ConfirmationModal';
import SeedInventoryButton from './SeedFridgeInventory/SeedInventoryButton';
import useSeedInventoryFlag from './SeedFridgeInventory/useSeedInventoryFlag';
import './ViewHealth.css';

const toastDefaultOptions = {
  className: 'prettyToast',
  position: 'top-center',
  autoClose: 6000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'light',
};

const InfoTooltip = ({ id, text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{text}</Tooltip>}>
    <span style={{ cursor: 'pointer' }}>
      <Glyphicon glyph="info-sign" style={{ fontSize: '12px', opacity: 0.6 }} />
    </span>
  </OverlayTrigger>
);

const TableRow = ({ header, value }) => (
  <tr>
    <th>{header}</th>
    <td>{value}</td>
  </tr>
);

export default function ViewHealth(props) {
  const FRIDGE_SERVICES = [
    {
      value: 'DisplayServices',
      label: 'Touchscreen Software',
      description:
        'This is the software that controls the display and touch input but NOT the hardware itself.',
    },
    {
      value: 'ReactUI',
      label: 'React UI',
      description:
        'The FF application that customers interact with to view the menu, place orders, and pick up reservations.',
    },
    {
      value: 'Jofemar',
      label: 'Jofemar',
      description:
        'Trigger a reset cycle of the elevator on the physical vending unit.',
    },
    {
      value: 'OutOfOrder',
      label: 'Out of Order',
      state: 'disabled',
      description:
        'Activate/Deactivate the feature showing customers that the fridge has an issue and a tech is being dispatched to service it.',
    },
    {
      value: 'FoodSafetyHold',
      label: 'Food Safety Hold',
      state: 'disabled',
      description:
        'Activate/Deactivate the feature indicating when a fridge not is able to serve customers due to food safety concerns.',
    },
    {
      value: 'ComputerSwap',
      label: 'Computer Swap',
      state: 'incomplete',
      description:
        'Ensures a new computer saves all the important data from the computer that was swapped.',
    },
    {
      value: 'JavaServer',
      label: 'Java Server',
      description:
        'The application that controls the vending hardware and processes customer transactions.',
      shouldShow: (healthData) => {
        const data = healthData?.data?.fridgeHealth?.[0]?.fridgeData?.[0];
        return (
          data?.allReasons?.includes('Layout Not Matched') ||
          data?.jofemarState === 'STATUS_FF_TIMEOUT'
        );
      },
    },
    // {
    //   value: 'FridgeAgent',
    //   label: 'Fridge Agent',
    //   description: 'Software that updates services within the Fridge.',
    // },
    // {
    //   value: 'PricingParser',
    //   label: 'Pricing Parser',
    //   description: 'Software that determines pricing and UI data.',
    // },
    // {
    //   value: 'PricingConsumer',
    //   label: 'Pricing Consumer',
    //   description: 'Software that updates pricing and UI data.',
    // },
    // {
    //   value: 'InventoryService',
    //   label: 'Inventory Service',
    //   description: 'The FF software that manages inventory within the Fridge.',
    // },
    // {
    //   value: 'Heartbeat',
    //   label: 'Heartbeat',
    //   description: 'Software that reports vital Fridge data.',
    // },
    // {
    //   value: 'CardReader',
    //   label: 'Card Reader',
    //   description: 'The card reader on the Fridge.',
    // },
    // {
    //   value: 'Redis',
    //   label: 'Redis',
    //   description: 'The database that stores the data for the Fridge.',
    // },
  ];
  const SPECIAL_RESTART_SERVICES = [
    'Jofemar',
    'OutOfOrder',
    'FoodSafetyHold',
    'ComputerSwap',
  ];
  const SEGMENT_EVENTS = [
    'sessionStarted',
    'productListViewed',
    'purchaseCompleted',
    'pickupAccepted',
  ];
  const eventDisplayNameMapping = {
    sessionStarted: 'Fridge Session Started',
    productListViewed: 'Fridge Menu Initialized',
    purchaseCompleted: 'Fridge Purchase Completed',
    pickupAccepted: 'Mobile Pickup Completed',
  };

  const [isLoading, setIsLoading] = useState(false);
  const [locationData, setLocationData] = useState({});
  const [isLiteFridge, setIsLiteFridge] = useState(false);

  const [healthData, setHealthData] = useState({
    data: { healthData: [] },
  });
  const [eventData, setEventData] = useState({});
  const [segmentEvents, setSegmentEvents] = useState({});

  const QueueMessageTypes = {
    InventoryService: 'InventoryService',
    PricingSchema: 'PricingSchema',
    FridgeAgent: 'FridgeAgent',
    JavaServer: '',
  };
  const [totalQueueMessages, setTotalQueueMessages] = useState({});
  const [queueMessagesLoading, setQueueMessagesLoading] = useState(false);
  const [outOfOrderButtonState, setOutOfOrderButtonState] = useState(false); // help to show which out of order button to show if clicked
  const [outOfOrderButtonInitialState, setOutOfOrderButtonInitialState] =
    useState(false); // determine which out of order button to show based on the data
  const [foodSafetyButtonState, setFoodSafetyButtonState] = useState(false); // help to show which food safety button to show if clicked
  const [foodSafetyButtonInitialState, setFfoodSafetyButtonInitialState] =
    useState(false); // determine which food safety button to show based on the data
  const [fridgeServiceConfirmModalShow, setFridgeServiceConfirmModalShow] =
    useState(false);
  const [resetFridgeServicesLoading, setResetFridgeServicesLoading] =
    useState(false);
  const [serviceToReset, setServiceToReset] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false); // help to prevent the data from keeping loading
  const [isAdmin, setIsAdmin] = useState(false); // determine whether the user is admin
  const [buttonAccess, setButtonAccess] = useState(false); // determine whether the user has access to the button
  const seedInventoryEnabled = useSeedInventoryFlag();
  const locationId = props.match.params[0];
  const hasPlaceholderIp =
    typeof locationData.ip === 'string' &&
    locationData.ip.includes('farmersfridge.chi');
  const hasNonLiveStatus =
    !!locationData.status && locationData.status !== 'LIVE';
  const determineState = (initialState, currentState) => {
    if (initialState) {
      return currentState ? 'ENABLE' : 'DISABLE';
    }
    return currentState ? 'DISABLE' : 'ENABLE';
  };

  const handlePermissions = (LDData) => {
    if (LDData) {
      if (LDData.admin) {
        setIsAdmin(true);
      } else {
        Object.keys(LDData.access).forEach((key) => {
          setButtonAccess((prev) => ({
            ...prev,
            [key]: LDData.access[key],
          }));
        });
      }
    }
  };

  useEffect(() => {
    const LDCUser = {
      key: Auth.user.attributes.email,
    };
    const ldclient = LDClient.initialize(
      process.env.REACT_APP_LAUNCH_DARKLY_CMS
        ? process.env.REACT_APP_LAUNCH_DARKLY_CMS
        : 'nlfaepofjeowiajfpoijewr',
      LDCUser,
    );
    ldclient.on('ready', () => {
      const allFlagsValue = ldclient.allFlags();
      if (allFlagsValue['reset-fridge-service']) {
        handlePermissions(allFlagsValue['reset-fridge-service']);
      }
    });
  }, []);

  useEffect(() => {
    if (!isLoadingData) {
      handleSubmit();
      setIsLoadingData(true);
      getLocationById();
      refreshQueueMessageStatus();
    }
    formatHealthTable();
  });

  const booleanFormatList = [
    'touchscreen_usb',
    'touchscreen_hdmi',
    'cardreader',
    'electrical_layout',
  ];
  const StyledTable = styledComponent('table', {
    '& table': {
      width: '100%',
    },
    tableLayout: 'fixed',
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'right',
    fontSize: '24px',
    '& td': {
      wordBreak: 'break-word',
    },
    '& td a': {
      textDecoration: 'underline',
    },
    '& pre': {
      fontSize: '24px',
      fontFamily: 'Courier New, Courier, monospace',
      textAlign: 'left',
      fontWeight: 'bold',
    },
  });

  const HealthWarningBanner = styledComponent('div', {
    '& .health-warning-card': {
      backgroundColor: theme.colors.dangerBg,
      border: `2px solid ${theme.colors.danger}`,
      borderRadius: theme.radii.card,
      padding: theme.spacing.cardPadding,
      marginBottom: theme.spacing.sectionGap,
      color: theme.colors.danger,
    },
    '& .health-warning-title': {
      fontSize: '22px',
      fontWeight: theme.fontWeights.semibold,
      marginBottom: theme.spacing.gapMd,
    },
    '& .health-warning-item': {
      fontSize: '16px',
      marginTop: theme.spacing.gapMd,
      color: theme.colors.textBody,
    },
    '& .health-warning-item a': {
      color: theme.colors.danger,
      textDecoration: 'underline',
      fontWeight: theme.fontWeights.semibold,
    },
    '& .health-warning-item code': {
      backgroundColor: theme.colors.bgMuted,
      padding: '2px 6px',
      borderRadius: theme.radii.badge,
      fontFamily: 'Courier New, Courier, monospace',
    },
  });

  const ButtonWrapper = styledComponent('div', {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    margin: '40px',
    '& .text': {
      fontSize: '20px',
      fontWeight: 'bold',
      display: 'flex',
      flexDirection: 'column',
    },
    '& button': {
      width: '30%',
    },
  });

  function stringtifyPhysicalLayout(layout) {
    const rowLength = layout?.length;
    const newLayout = [];
    for (let i = 0; i < rowLength; i++) {
      const eachRow = layout[i].rowValue;
      let newRow = '';
      const columnLength = eachRow.length;
      for (let j = 0; j < columnLength; j++) {
        newRow = `${newRow}( ${eachRow[j].x}, ${eachRow[j].y} )`;
      }
      newLayout.push(newRow);
    }
    return newLayout;
  }

  function formatTimestamp(timestamp) {
    // workaround for when out of order/safety hold triggered from slack changes date time format
    if (timestamp.includes('Z')) {
      timestamp = timestamp.split('Z')[0];
    }
    return moment.parseZone(timestamp).format('MM/DD/YYYY HH:mm');
  }

  function awsDatetimeToCT(utcDatetime) {
    return moment
      .utc(utcDatetime)
      .tz('America/Chicago')
      .format('MM/DD/YYYY HH:mm');
  }

  function determineStylingTimestamp(eventBool, timestamp) {
    if (eventBool === true) {
      return 'red';
    }
    if (timestamp) {
      const days = moment().diff(timestamp, 'days');
      if (days < 1) {
        return 'orange';
      }
    }
    return 'green';
  }

  function determineStylingBool(eventBool, goodState) {
    if (eventBool === goodState) {
      return 'green';
    }
    return 'red';
  }

  function determineStylingTemp(eventTemp) {
    let color = 'red';
    if (eventTemp <= 38.0) {
      color = 'green';
    } else if (eventTemp <= 41.0) {
      color = 'orange';
    }
    return color;
  }

  function determineStylingNetworkSpeed(speed) {
    let color = 'red';
    if (speed === 'None') {
      color = 'red';
    } else if (speed < 0.5) {
      color = 'green';
    }
    return color;
  }

  function determineStylingJofemar(jofemarState) {
    let color = 'red';
    if (jofemarState === 'STATUS_0_READY') {
      color = 'green';
    }
    return color;
  }

  function formatHealthTable() {
    if (healthData.data.fridgeHealth) {
      const table = document.getElementById('health_table');
      if (table) {
        for (let row = 0; row < table.children.length; row++) {
          const el = table.children[row];
          if (booleanFormatList.includes(el.children[0].id)) {
            el.style.color = determineStylingBool(
              el.children[1].innerText,
              'true',
            );
          } else {
            switch (el.children[0].id) {
              case 'temp':
                el.style.color = determineStylingTemp(el.children[1].innerText);
                break;
              case 'jofemar':
                el.style.color = determineStylingJofemar(
                  el.children[1].innerText,
                );
                break;
              case 'out_of_order':
                if (el.children[1].innerText !== 'No data') {
                  setOutOfOrderButtonInitialState(
                    healthData.data.fridgeHealth[0].fridgeData[0].outOfOrder,
                  );
                  el.style.color = determineStylingTimestamp(
                    healthData.data.fridgeHealth[0].fridgeData[0].outOfOrder,
                    healthData.data.fridgeHealth[0].fridgeData[0].outOfOrderTs,
                  );
                } else {
                  el.style.color = 'red';
                }
                break;
              case 'safety_hold':
                if (el.children[1].innerText !== 'No data') {
                  setFfoodSafetyButtonInitialState(
                    healthData.data.fridgeHealth[0].fridgeData[0].safetyHold,
                  );
                  el.style.color = determineStylingTimestamp(
                    healthData.data.fridgeHealth[0].fridgeData[0].safetyHold,
                    healthData.data.fridgeHealth[0].fridgeData[0].safetyHoldTs,
                  );
                } else {
                  el.style.color = 'red';
                }
                break;
              case 'network_speed':
                el.style.color = determineStylingNetworkSpeed(
                  el.children[1].innerText,
                );
                break;
              case 'inaccessible':
                el.style.color = el.children[1].innerText.startsWith('false')
                  ? 'green'
                  : 'red';
                break;
              case 'activeTicket':
                el.style.color = el.children[1].innerText.startsWith('false')
                  ? 'green'
                  : 'red';
                break;
              case 'all_reasons':
                if (el.children[1].innerText.startsWith('No issues')) {
                  el.style.color = 'green';
                } else {
                  el.style.color = 'red';
                }
                break;
              case 'activeWorkOrders':
                if (el.children[1].innerText === 'No data') {
                  el.style.color = 'red';
                } else if (
                  el.children[1].innerText.startsWith('No open work orders')
                ) {
                  el.style.color = 'green';
                } else if (
                  healthData.data.fridgeHealth[0].fridgeData[0].activeTicket ===
                  true
                ) {
                  el.style.color = 'red';
                } else {
                  el.style.color = 'orange';
                }
                break;
              case 'physical_layout':
                if (el.children[1].innerText === 'No data') {
                  el.style.color = 'red';
                }
              // eslint-disable-next-line no-fallthrough
              default:
                break;
            }
          }
        }
      }
    }
  }
  function buildConfirmationString() {
    let msg = `Are you sure you want to `;
    if (serviceToReset.value === 'ComputerSwap') {
      msg += `complete ${serviceToReset.label}`;
    } else if (
      serviceToReset.value === 'OutOfOrder' ||
      serviceToReset.value === 'FoodSafetyHold'
    ) {
      msg += `${determineState(
        serviceToReset.value === 'FoodSafetyHold'
          ? foodSafetyButtonInitialState
          : outOfOrderButtonInitialState,
        serviceToReset.value === 'FoodSafetyHold'
          ? foodSafetyButtonState
          : outOfOrderButtonState,
      )} ${serviceToReset.label}`;
    } else {
      msg += `reset ${serviceToReset.label}`;
    }
    return msg;
  }
  async function getLocationById() {
    const id = props.match.params[0];
    try {
      const response = await Location(id);
      if (response.length === 0 || !response.fridgeModel) {
        const locationData = response;
        setLocationData(locationData[0]);
        if (
          locationData[0].fridgeModel &&
          locationData[0].fridgeModel.toLowerCase().includes('lite')
        ) {
          setIsLiteFridge(true);
        } else {
          setIsLiteFridge(false);
        }
        return response;
      }
    } catch (e) {
      console.error(e);
      this.setState({
        alertModal: {
          mode: true,
          text: 'Unable to load location ${e.message}',
        },
      });
    }
  }

  async function handleSubmit(event) {
    setIsLoading(true);
    try {
      const location = props.match.params[0];
      const fridgeHealthFilter = {
        locationIds: [location],
      };
      const result = await FridgeHealth(fridgeHealthFilter);
      setHealthData(result);
      const fridgeEventFilter = {
        location,
        eventTypes: ['CheckpointEvent'],
      };
      const eventResult = await GetFridgeEvents(fridgeEventFilter);
      setEventData(eventResult);

      const fridgeSegmentEventFilter = {
        location,
        fridgeSegmentEventType: SEGMENT_EVENTS,
      };
      const segmentEventResult = await GetFridgeSegmentEvents(
        fridgeSegmentEventFilter,
      );
      setSegmentEvents(segmentEventResult);

      setIsLoading(false);
    } catch (e) {
      alert('Oops, there was an error loading your request.');
      setIsLoading(false);
    }
  }

  async function computerSwapCompleteAction() {
    try {
      const locationId = props.match.params[0];
      const res = await CompleteComputerSwapRequest(locationId);
      if (res.data.sendConfirmedInventoryAndConfirmedReservation) {
        toast.success(
          `Computer swap completed successfully`,
          toastDefaultOptions,
        );
      } else {
        toast.error(
          `Oops, there was an error when creating the inventory service queue message.`,
          toastDefaultOptions,
        );
      }
    } catch (e) {
      toast.error(
        `Oops, there was an error with making computer swap complete request.`,
        toastDefaultOptions,
      );
    }
    setFridgeServiceConfirmModalShow(false);
  }

  async function refreshQueueMessageStatus() {
    setTotalQueueMessages({});
    setQueueMessagesLoading(true);
    const locationId = props.match.params[0];
    try {
      Object.keys(QueueMessageTypes).forEach(async (typeKey) => {
        const type = QueueMessageTypes[typeKey];
        let nextToken = null;
        let totalMessages = 0;
        do {
          const res = await GetQueueMessages(locationId, type, nextToken);
          totalMessages += res.data.readQueueMessages.queueMessages.length;
          nextToken = res.data.readQueueMessages.nextToken;
        } while (nextToken);

        setTotalQueueMessages((prev) => ({
          ...prev,
          [typeKey]: totalMessages,
        }));
      });
    } catch (e) {
      console.error(e);
    }
    setQueueMessagesLoading(false);

    return false;
  }

  function handleFridgeServiceState(service) {
    if (service.value === 'FoodSafetyHold') {
      return `${determineState(
        foodSafetyButtonInitialState,
        foodSafetyButtonState,
      )} Food Safety Hold`;
    }
    if (service.value === 'OutOfOrder') {
      return `
      ${determineState(
        outOfOrderButtonInitialState,
        outOfOrderButtonState,
      )} Out of Order`;
    }
    if (service.value === 'ComputerSwap') {
      return 'Complete Computer Swap';
    }
  }

  async function handleFridgeServiceResetSelect(service) {
    setFridgeServiceConfirmModalShow(true);
    setServiceToReset(service);
  }
  async function handleFridgeServiceReset() {
    setResetFridgeServicesLoading(true);
    try {
      const location = props.match.params[0];
      const inputInfo = {
        location,
        message: {
          messageType: 'Resource',
          payload: {
            operation: serviceToReset.value,
            resource: 'ServiceRestart',
          },
        },
      };
      if (serviceToReset.value === 'Jofemar') {
        inputInfo.message.payload = {
          operation: null,
          resource: 'VendingUnitReset',
        };
      } else if (serviceToReset.value === 'FoodSafetyHold') {
        inputInfo.message.payload = {
          operation: determineState(
            foodSafetyButtonInitialState,
            foodSafetyButtonState,
          ),
          resource: 'SafetyHold',
        };
      } else if (serviceToReset.value === 'OutOfOrder') {
        inputInfo.message.payload = {
          operation: determineState(
            outOfOrderButtonInitialState,
            outOfOrderButtonState,
          ),
          resource: 'OutOfOrder',
        };
      } else if (serviceToReset.value === 'ComputerSwap') {
        computerSwapCompleteAction();
        return;
      }
      await CreateQueueMessage(inputInfo);
      let msg = `Reset ${serviceToReset.label} successful`;
      if (
        SPECIAL_RESTART_SERVICES.includes(serviceToReset.value) &&
        serviceToReset.value !== 'Jofemar'
      ) {
        msg = `${serviceToReset.label} ${determineState(
          serviceToReset.value === 'FoodSafetyHold'
            ? foodSafetyButtonInitialState
            : outOfOrderButtonInitialState,
          serviceToReset.value === 'FoodSafetyHold'
            ? foodSafetyButtonState
            : outOfOrderButtonState,
        )}D successfully`;
      }
      msg = `${msg}. This command should be processed within 5 minutes.`;
      toast.success(msg, toastDefaultOptions);
    } catch (e) {
      toast.error(
        `There was an issue resetting the ${serviceToReset.label}`,
        toastDefaultOptions,
      );
    }
    if (serviceToReset.value === 'FoodSafetyHold') {
      setFoodSafetyButtonState(!foodSafetyButtonState);
    } else if (serviceToReset.value === 'OutOfOrder') {
      setOutOfOrderButtonState(!outOfOrderButtonState);
    }
    setResetFridgeServicesLoading(false);
    setFridgeServiceConfirmModalShow(false);
  }
  const fridgeEventDisplayInfo = {
    CheckpointEvent: {
      displayName: 'Restocking Event',
      description: 'Last time Inventory got updated on the Fridge UI',
    },
  };

  const renderFridgeEventData = () => {
    if (eventData && eventData.data && eventData.data.getFridgeEvents) {
      const timezone =
        healthData.data?.fridgeHealth?.[0]?.fridgeData?.[0]?.timezone;
      return eventData.data.getFridgeEvents.map((data, index) => {
        const eventInfo = fridgeEventDisplayInfo[data.eventType] || {
          displayName: data.eventType,
          description: null,
        };
        const rawTimestamp = data.timestamp.split(`${data.location}-`)[1];

        return (
          <tr key={index}>
            <th>
              {eventInfo.displayName}{' '}
              <InfoTooltip
                id="restocking-event-tooltip"
                text="Indicates the last time a driver restocking event got updated on the Fridge UI"
              />
            </th>
            <td>
              {rawTimestamp && timezone
                ? `Most recently at ${formatTimestamp(
                    rawTimestamp,
                  )} [${timezone}]`
                : 'No data'}
            </td>
          </tr>
        );
      });
    }
  };

  const renderFridgeSegmentEvents = () => {
    if (segmentEvents?.data?.getFridgeSegmentEvents) {
      const timezone =
        healthData.data?.fridgeHealth?.[0]?.fridgeData?.[0]?.timezone;
      return SEGMENT_EVENTS.map((eventName, index) => {
        const segmentEventData = segmentEvents.data.getFridgeSegmentEvents.find(
          ({ eventType }) => eventType === eventName,
        );
        if (segmentEventData) {
          const { eventType, date, payload } = segmentEventData;
          const actionDescriptorMatch = payload.match(
            /Action Descriptor=([^,]*)/,
          );
          const actionDescriptor = actionDescriptorMatch
            ? actionDescriptorMatch[1].trim()
            : null;
          const eventDisplayName = eventDisplayNameMapping[eventType];
          if (index === 2 && actionDescriptor !== 'Fridge Purchase') {
            return <TableRow header={eventDisplayName} value="No Event" />;
          }
          return (
            <tr>
              <th>
                {eventDisplayName}{' '}
                {actionDescriptor ? `(${actionDescriptor})` : ''}
              </th>
              <td>
                {date && timezone
                  ? `Most recently at ${awsDatetimeToCT(date)} [${timezone}]`
                  : 'No data'}
              </td>
            </tr>
          );
        }
        return (
          <TableRow
            header={eventDisplayNameMapping[eventName]}
            value="No Event"
          />
        );
      });
    }
    return null;
  };

  return (
    <>
      <div>
        {(hasPlaceholderIp || hasNonLiveStatus) && (
          <HealthWarningBanner>
            <div className="health-warning-card">
              <div className="health-warning-title">
                Attention Required — Location Configuration Issue
              </div>
              {hasPlaceholderIp && (
                <div className="health-warning-item">
                  This location&apos;s IP address is set to a placeholder value
                  (<code>{locationData.ip}</code>). If this is a new install,
                  please{' '}
                  <Link to={`/locations/detail/${locationId}`}>
                    update the IP address field in CMS
                  </Link>
                  .
                </div>
              )}
              {hasNonLiveStatus && (
                <div className="health-warning-item">
                  This location&apos;s status is{' '}
                  <strong>{locationData.status}</strong>, not{' '}
                  <strong>LIVE</strong>. Please{' '}
                  <Link to={`/locations/detail/${locationId}`}>
                    update the status to LIVE
                  </Link>
                  .
                </div>
              )}
            </div>
          </HealthWarningBanner>
        )}
        {!healthData.data.fridgeHealth && (
          <div>
            <h3>Loading...</h3>
          </div>
        )}

        {healthData.data.fridgeHealth &&
          healthData.data.fridgeHealth[0].fridgeData.length <= 0 && (
            <div>
              <h3>No data to display.</h3>
            </div>
          )}
        <div>
          {healthData.data.fridgeHealth &&
            // TODO: rewrite map without shadow naming
            // eslint-disable-next-line no-shadow
            healthData.data.fridgeHealth[0].fridgeData.map((healthData) => (
              <div key={healthData.locationId}>
                <h3>
                  {healthData.locationId}
                  {healthData.timestamp
                    ? ` as of ${moment
                        .unix(parseInt(healthData.timestamp.toString()))
                        .fromNow()}`
                    : ''}
                </h3>
                <div className="button-container">
                  <Link to={`/locations/inventory/${healthData.locationId}`}>
                    <Button bsStyle="primary" className="button-spacing">
                      Inventory
                    </Button>
                  </Link>
                  <Link to={`/locations/detail/${healthData.locationId}`}>
                    <Button bsStyle="primary">Location Detail</Button>
                  </Link>
                  {seedInventoryEnabled && (
                    <span style={{ marginLeft: '8px' }}>
                      <SeedInventoryButton
                        locationId={healthData.locationId}
                        enabled={seedInventoryEnabled}
                      />
                    </span>
                  )}
                </div>
                <StyledTable id="health_table">
                  <tr>
                    <th>&nbsp;</th>
                    <th>Status</th>
                  </tr>
                  <tr>
                    <th id="temp">Temperature Reading</th>
                    <td>
                      {healthData.temperature !== null
                        ? healthData.temperature.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="target_temp">Target Temperature</th>
                    <td>
                      {healthData.temperatureSetpoint !== null
                        ? healthData.temperatureSetpoint.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="touchscreen_usb">Touchscreen Connected</th>
                    <td>
                      {healthData.elo !== null
                        ? healthData.elo.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="touchscreen_hdmi">HDMI Connected</th>
                    <td>
                      {healthData.hdmi !== null
                        ? healthData.hdmi.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="cardreader">Card Reader Connected</th>
                    <td>
                      {healthData.cardReader !== null
                        ? healthData.cardReader.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="electrical_layout">Electrical Layout Connected</th>
                    <td>
                      {healthData.electricalLayout !== null
                        ? healthData.electricalLayout.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="jofemar">Vending State</th>
                    <td>
                      {healthData.jofemarState !== null
                        ? healthData.jofemarState.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="network_speed">Network Speed</th>
                    <td>
                      {healthData.networkSpeed !== null
                        ? healthData.networkSpeed.previousSpeed.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="out_of_order">Out of Order</th>
                    <td>
                      {healthData.outOfOrder !== null &&
                      healthData.outOfOrderTs !== null &&
                      healthData.timezone !== null
                        ? `${healthData.outOfOrder.toString()} since ${formatTimestamp(
                            healthData.outOfOrderTs.toString(),
                          )} [${healthData.timezone}]`
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="safety_hold">Safety Hold</th>
                    <td>
                      {healthData.safetyHold !== null &&
                      healthData.safetyHoldTs !== null &&
                      healthData.timezone !== null
                        ? `${healthData.safetyHold.toString()} since ${formatTimestamp(
                            healthData.safetyHoldTs.toString(),
                          )} [${healthData.timezone}]`
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="inaccessible">
                      Inaccessible{' '}
                      <InfoTooltip
                        id="inaccessible-tooltip"
                        text="Data sourced from FridgeHealthStatus which refreshes every ~15 min"
                      />
                    </th>
                    <td>
                      {healthData.inaccessible !== null
                        ? `${healthData.inaccessible.toString()} as of ${moment
                            .unix(
                              parseInt(
                                healthData.healthStatusTimestamp?.toString(),
                              ),
                            )
                            .fromNow()}`
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="activeTicket">
                      Active eMaint Ticket{' '}
                      <InfoTooltip
                        id="activeTicket-tooltip"
                        text="Data sourced from FridgeHealthStatus which refreshes every ~15 min"
                      />
                    </th>
                    <td>
                      {healthData.activeTicket !== null
                        ? `${healthData.activeTicket.toString()} as of ${moment
                            .unix(
                              parseInt(
                                healthData.healthStatusTimestamp?.toString(),
                              ),
                            )
                            .fromNow()}`
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="activeWorkOrders">
                      Open Work Orders{' '}
                      <InfoTooltip
                        id="activeWorkOrders-tooltip"
                        text="Open work orders from the latest FridgeHealthStatus, refreshes every ~15 min"
                      />
                    </th>
                    <td>
                      {!healthData.activeWorkOrders ? (
                        'No data'
                      ) : healthData.activeWorkOrders.length > 0 ? (
                        <div>
                          {healthData.activeWorkOrders.map((wo, index) => (
                            <div key={wo.wo || index} className="workOrderItem">
                              <div>
                                <strong>WO #:</strong>{' '}
                                {wo.wo ? (
                                  <a
                                    href={`https://x43.emaint.com/wc.dll?X3~emproc~x3Hubv2#/WORK/${wo.wo}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="workOrderLink"
                                  >
                                    {wo.wo}
                                  </a>
                                ) : (
                                  'N/A'
                                )}
                              </div>
                              <div>
                                <strong>Last Updated:</strong>{' '}
                                {wo.date_wo
                                  ? `${moment(wo.date_wo).format(
                                      'YYYY-MM-DD',
                                    )}${wo.time ? ` ${wo.time}` : ''}${
                                      wo.time_zone ? ` ${wo.time_zone}` : ''
                                    }`
                                  : 'Not available'}
                              </div>
                              <div>
                                <strong>Scheduled:</strong>{' '}
                                {wo.sch_date
                                  ? moment(wo.sch_date).format('YYYY-MM-DD')
                                  : 'Not scheduled'}
                              </div>
                              <div>
                                <strong>Assigned To:</strong>{' '}
                                {wo.assignto || 'Not yet assigned'}
                              </div>
                              <div>
                                <strong>Description:</strong>{' '}
                                {wo.brief_desc || 'No description provided'}
                              </div>
                            </div>
                          ))}
                          <div>
                            {`as of ${moment
                              .unix(
                                parseInt(
                                  healthData.healthStatusTimestamp?.toString(),
                                ),
                              )
                              .fromNow()}`}
                          </div>
                        </div>
                      ) : (
                        `No open work orders as of ${moment
                          .unix(
                            parseInt(
                              healthData.healthStatusTimestamp?.toString(),
                            ),
                          )
                          .fromNow()}`
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th id="all_reasons">
                      All Issue Reasons{' '}
                      <InfoTooltip
                        id="all_reasons-tooltip"
                        text="Data sourced from FridgeHealthStatus which refreshes every ~15 min"
                      />
                    </th>
                    <td>
                      {healthData.allReasons !== null
                        ? healthData.allReasons.length !== 0
                          ? `${healthData.allReasons.join(', ')} as of ${moment
                              .unix(
                                parseInt(
                                  healthData.healthStatusTimestamp?.toString(),
                                ),
                              )
                              .fromNow()}`
                          : `No issues as of ${moment
                              .unix(
                                parseInt(
                                  healthData.healthStatusTimestamp?.toString(),
                                ),
                              )
                              .fromNow()}`
                        : 'No data'}
                    </td>
                  </tr>
                  <tr>
                    <th id="server_version">Java Server Version</th>
                    <td>
                      {healthData.serverVersion !== null
                        ? healthData.serverVersion.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  {renderFridgeEventData()}
                  {renderFridgeSegmentEvents()}
                  <tr>
                    <th id="physical_layout">
                      Fridge Physical Connected Layout
                    </th>
                    <td className="physical-layout-cell">
                      <table>
                        {healthData.connectedLayout?.length !== 0
                          ? stringtifyPhysicalLayout(
                              healthData.connectedLayout,
                            ).map((item) => {
                              return (
                                <tr>
                                  <td>{item}</td>
                                </tr>
                              );
                            })
                          : 'No data'}
                      </table>
                    </td>
                  </tr>
                  <tr>
                    {/* Fridge Model Row */}
                    <th id="fridge_model">Fridge Model</th>
                    <td>
                      {locationData.fridgeModel
                        ? locationData.fridgeModel.toString()
                        : 'No data'}
                    </td>
                  </tr>
                  {isLiteFridge && (
                    <>
                      <tr>
                        <th id="lite_fridge_state">Lite Fridge State</th>
                        <td>
                          <pre>
                            {healthData.liteFridgeState
                              ? JSON.stringify(
                                  healthData.liteFridgeState,
                                  null,
                                  2,
                                )
                              : 'No Data'}
                          </pre>
                        </td>
                      </tr>
                      <tr>
                        <th id="recycle_door_state">Recycle Door State</th>
                        <td>
                          <pre>
                            {healthData.recycleDoorState
                              ? JSON.stringify(
                                  healthData.recycleDoorState,
                                  null,
                                  2,
                                )
                              : 'No Data'}
                          </pre>
                        </td>
                      </tr>
                      <tr>
                        <th id="sundry_door_state">Sundry Door State</th>
                        <td>
                          <pre>
                            {healthData.sundryDoorState
                              ? JSON.stringify(
                                  healthData.sundryDoorState,
                                  null,
                                  2,
                                )
                              : 'No Data'}
                          </pre>
                        </td>
                      </tr>
                    </>
                  )}
                </StyledTable>
              </div>
            ))}
        </div>
        <br />
        <ButtonWrapper>
          <div className="text">
            <div>
              Total Java Server Queue Messages : {totalQueueMessages.JavaServer}
            </div>
            <br />
            <div>
              Total Inventory Service Queue Messages :{' '}
              {totalQueueMessages.InventoryService}
            </div>
            <br />
            <div>
              Total Pricing Schema Queue Messages :{' '}
              {totalQueueMessages.PricingSchema}
            </div>
            <br />
            <div>
              Total Fridge Agent Queue Messages :{' '}
              {totalQueueMessages.FridgeAgent}
            </div>
          </div>
          <LoaderButton
            block
            type="submit"
            bsSize="small"
            bsStyle="primary"
            isLoading={queueMessagesLoading}
            text="Refresh Data"
            loadingText="Loading..."
            onClick={() => refreshQueueMessageStatus()}
            className="button"
          />
        </ButtonWrapper>

        <div>
          {(Object.keys(buttonAccess).length > 0 || isAdmin) && (
            <>
              <h1>Fridge Commands</h1>
              {FRIDGE_SERVICES.map((service) => {
                const shouldShowButton = service.shouldShow
                  ? service.shouldShow(healthData)
                  : true;
                return (
                  <>
                    {(buttonAccess[service.value] || isAdmin) &&
                      shouldShowButton && (
                        <>
                          <h3>{service.label}</h3>
                          <h5>{service.description}</h5>
                          <LoaderButton
                            type="submit"
                            bsSize="medium"
                            bsStyle="primary"
                            text={
                              service.state
                                ? handleFridgeServiceState(service)
                                : `Reset ${service.label}`
                            }
                            loadingText="Please Wait Until Action Complete"
                            onClick={() =>
                              handleFridgeServiceResetSelect(service)
                            }
                          />
                          <br />
                        </>
                      )}
                  </>
                );
              })}
            </>
          )}
        </div>
      </div>
      <br />
      <ConfirmationModal
        text=""
        show={fridgeServiceConfirmModalShow}
        onHide={() => setFridgeServiceConfirmModalShow(false)}
        onConfirm={() => handleFridgeServiceReset()}
        loading={resetFridgeServicesLoading}
      >
        {buildConfirmationString()}
        <br />
      </ConfirmationModal>
    </>
  );
}
